import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AttentionMessage,
  ResponsiveDataTable,
  Typography,
  useThemedValueSwitch,
} from '@tq/ui-components';

import { EmptyDataCard, hiddenTableHeaderLabel } from 'components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { Consultation, Document } from 'types';
import { DocumentsMobileCard, useDocumentFilters } from '../Documents';
import { usePlansMenuOptions } from '../../hooks';
import { renderDocumentMenuActionButton } from '../../util';
import { useFormatLocaleDate } from 'config/DateFormats/useFormatLocaleDate';

export type PlansProps = {
  consultation?: Consultation;
  plans?: Document[];
};

export const Plans: React.FC<PlansProps> = ({ consultation, plans }) => {
  const { t } = useTranslation('case_details_plans');
  const options = usePlansMenuOptions();
  const { isNI } = useThemedValueSwitch();
  const isMobile = useIsMobile();
  const formatLocaleDate = useFormatLocaleDate();
  const filters = useDocumentFilters(plans, false);
  const filterColumnLayout = isMobile ? '100%' : '33% 65%';

  return (
    <>
      {isMobile && (
        <Typography mx={4} mb={4}>
          <h2>{t('plans', 'Plans')}</h2>
        </Typography>
      )}
      {!plans?.length ? (
        <EmptyDataCard message={t('no_plans_message', 'No plans available')} />
      ) : (
        <>
          {isNI && (
            <AttentionMessage
              status='info'
              variant='inline'
              mb={5}
              title={t(
                'files_measuring_tool_message',
                'You can access a tool to measure the plans and drawings below by selecting the measuring tool option on the file menu'
              )}
            />
          )}
          <ResponsiveDataTable
            size='sm'
            Card={(plan) => (
              <DocumentsMobileCard
                key={plan.id}
                consultation={consultation}
                document={plan}
                options={options}
              />
            )}
            filterColumnLayout={filterColumnLayout}
            filters={filters}
            initialState={{
              sortBy: [
                {
                  id: 'datePublished',
                  desc: true,
                },
              ],
            }}
            data={plans}
            columns={[
              {
                accessor: 'datePublished',
                Cell: (cellProps: { value: string }) => formatLocaleDate(cellProps.value),
                Header: () => t('date_published', 'Date published'),
                width: 181,
              },
              {
                accessor: 'documentType',
                Cell: (cellProps: { value: string }) => cellProps.value,
                Header: () => t('document_type', 'Document type'),
                width: 'auto',
              },
              {
                accessor: 'drawingNumber',
                disableSortBy: true,
                Header: () => t('drawing_number', 'Drawing number'),
                width: 'auto',
              },
              {
                accessor: 'description',
                disableSortBy: true,
                Header: () => t('document_description', 'Document description'),
                width: 'auto',
              },
              {
                accessor: 'actions',
                Cell: (cellProps: { row: { original: Document } }) =>
                  renderDocumentMenuActionButton(
                    consultation,
                    options,
                    cellProps.row.original
                  ),
                disableSortBy: true,
                Header: () => hiddenTableHeaderLabel(t('actions_label', 'actions')),
                width: 60,
              },
            ]}
          />
        </>
      )}
    </>
  );
};
